/**
 * WebGPU implementation of the Genesys renderer.
 * Extends THREE.WebGPURenderer and implements IGenesysRenderer interface.
 *
 * **XR/VR Support**: WebGPU XR support is still experimental in Three.js.
 * For VR applications, use WebGL renderer (set xrEnabled: true in RendererOptions
 * to automatically fall back to WebGL).
 */

import * as THREE from 'three';
import { TiledLighting } from 'three/addons/lighting/TiledLighting.js';
import TiledLightsNode from 'three/addons/tsl/lighting/TiledLightsNode.js';
import { WebGPURenderer } from 'three/webgpu';

import { getOrCreateWebGpuMatcapFromSource } from '../runtime/materials/matcapDualBackend.js';
import { MatcapShaderMaterial } from '../runtime/materials/MatcapShaderMaterial.js';
import { getOrCreateWebGpuWorldNormal, WORLD_NORMAL_MATERIAL_NAME } from '../runtime/materials/worldNormalDualBackend.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';

import type { IGenesysRenderer } from './IGenesysRenderer.js';
import type { Lighting } from 'three/webgpu';


/**
 * Options for WebGPU renderer (supports both native WebGPU and WebGL backend via forceWebGL)
 *
 * Note: `preserveDrawingBuffer` from WebGLRenderer is not supported. For screenshots,
 * render to a texture or capture before the next frame.
 */
export interface WebGPURendererParameters {
  /** (Optional) Canvas element to render to (required for R3F integration) */
  canvas?: HTMLCanvasElement;
  /** Enable antialiasing */
  antialias?: boolean;
  /** Enable alpha channel */
  alpha?: boolean;
  /** Enable depth buffer */
  depth?: boolean;
  /** Enable stencil buffer */
  stencil?: boolean;
  /** Power preference hint */
  powerPreference?: 'high-performance' | 'low-power';
  /** Force WebGL fallback even when WebGPU is available */
  forceWebGL?: boolean;
  /**
   * Custom lighting system for WebGPU renderer.
   * Setting this ensures lighting is configured before any scene objects are loaded,
   * avoiding initialization timing issues with custom lighting like TiledLighting.
   */
  lighting?: Lighting;
}

export class GenesysWebGPURenderer extends WebGPURenderer implements IGenesysRenderer {
  private _sizeCache = new THREE.Vector2();

  private constructor(options?: WebGPURendererParameters) {
    super({
      canvas: options?.canvas,
      antialias: options?.antialias ?? true,
      alpha: options?.alpha ?? false,
      depth: options?.depth ?? true,
      stencil: options?.stencil ?? false,
      powerPreference: options?.powerPreference,
      forceWebGL: options?.forceWebGL ?? false,
    });

    ObjectTracker.track(this);

    // Initialize shadow map
    this.shadowMap.enabled = true;
    this.shadowMap.type = THREE.PCFShadowMap;

    // Initialize tone mapping
    this.toneMapping = THREE.NoToneMapping;
    this.toneMappingExposure = 1.0;

    const library = this.library as {
      fromMaterial: (material: THREE.Material) => THREE.Material | null;
    };
    const baseFromMaterial = library.fromMaterial.bind(library);
    library.fromMaterial = (material: THREE.Material) => {
      if (material instanceof MatcapShaderMaterial) {
        return getOrCreateWebGpuMatcapFromSource(material);
      }
      if (material instanceof THREE.ShaderMaterial && material.name === WORLD_NORMAL_MATERIAL_NAME) {
        return getOrCreateWebGpuWorldNormal();
      }
      return baseFromMaterial(material);
    };
  }

  /**
   * Create and initialize a WebGPU renderer.
   * @param options - WebGPU renderer options
   * @returns Initialized GenesysWebGPURenderer
   */
  public static async create(options?: WebGPURendererParameters): Promise<GenesysWebGPURenderer> {
    const backendType = options?.forceWebGL ? 'WebGL' : 'WebGPU';
    console.log(`[GenesysWebGPURenderer] Renderer initialized with ${backendType} backend.`);
    const renderer = new GenesysWebGPURenderer(options);

    // Apply custom lighting immediately after creation
    // This ensures lighting is ready before any scene objects are loaded
    if (options?.lighting) {
      renderer.lighting = options.lighting;
    }

    await renderer.init();
    return renderer;
  }

  @THREE.profile
  public beginRenderFrame(): void {
    // WebGPU doesn't need the same auto-instancing as WebGL
    // GPU-driven rendering handles this more efficiently
  }

  @THREE.profile
  public endRenderFrame(): void {
    // No-op for WebGPU
  }

  public asWebGL(): THREE.WebGLRenderer | null {
    return null;
  }

  public asWebGPU(): WebGPURenderer | null {
    return this;
  }

  public override disposeSceneResources(scene: THREE.Scene): void {
    super.disposeSceneResources(scene);
  }

  @THREE.profile
  public override render(scene: THREE.Scene, camera: THREE.Camera): void {
    /*
    * This is a stupid hack we must do for the TiledLightsNode implementation
    * It serves 2 purposes:
    * 1. Make sure the node is initialized before use (the setSize call ensures it)
    *    otherwise it may crash in customCacheKey due to this._compute being null...
    * 2. Make sure the tile buffer is correctly resized when the screen size changes
    * The TiledLightsNode caches the last size internally and will no-op if the size doesn't change
    * This is why it's fine to run this code in the tick
    */
    if (this.lighting instanceof TiledLighting) {
      const node = this.lighting.getNode(scene);
      if (node instanceof TiledLightsNode) {
        this.getSize(this._sizeCache);
        node.setSize(this._sizeCache.x, this._sizeCache.y);
      }
    }
    super.render(scene, camera);
  }
}
